/**
 * Paycheck periods. Ported from `PaySchedule.swift`.
 *
 * Landings come from `getPayDays()`. A period runs from a pay day through the
 * day before the next one, including across a month boundary.
 */

import { roundCents } from './money';
import {
  daysBetweenLedgerDates,
  lastDayOfMonth,
  ledgerToday,
  ledgerYear,
  parseLedgerDate,
  partsInLedgerZone,
  YearMonthDay,
  ymd,
} from './ledger';
import { monthDay } from './transaction-display';

export interface PaycheckPeriod {
  /** Inclusive `YYYY-MM-DD`. */
  start: string;
  /** Inclusive `YYYY-MM-DD`. */
  end: string;
  /**
   * How long the window is. A month-long one belongs to an item funded once a
   * month, which has to make that funding last past the paycheck that gave it.
   * Absent means the ordinary half-month a paycheck covers.
   */
  span?: 'paycheck' | 'month';
}

const DEFAULT_PAY_DAYS = [1, 16];
let currentPayDays = DEFAULT_PAY_DAYS;

export function normalizePayDays(days: readonly number[]): number[] {
  const unique = [...new Set(
    days
      .filter((day) => Number.isInteger(day))
      .map((day) => Math.min(31, Math.max(1, day)))
  )].sort((a, b) => a - b);
  if (unique.length === 0) return [...DEFAULT_PAY_DAYS];
  return unique.slice(0, 8);
}

export function setPayDays(days: readonly number[] | null | undefined): number[] {
  currentPayDays = normalizePayDays(days ?? DEFAULT_PAY_DAYS);
  return currentPayDays;
}

export function getPayDays(): number[] {
  return currentPayDays;
}

export function resetPayDays(): number[] {
  currentPayDays = [...DEFAULT_PAY_DAYS];
  return currentPayDays;
}

const PREFERRED_PAY_DAYS = [1, 15, 16, 8, 22, 5, 20, 10, 25, 28];

/** First unused day, preferring common pay dates. */
export function suggestedPayDay(days: readonly number[] = getPayDays()): number | null {
  const occupied = new Set(normalizePayDays(days));
  if (occupied.size >= 8) return null;
  const candidates = [...PREFERRED_PAY_DAYS, ...Array.from({ length: 31 }, (_, index) => index + 1)];
  return candidates.find((day) => !occupied.has(day)) ?? null;
}

function landings(year: number, month: number): number[] {
  const last = lastDayOfMonth(year, month);
  return [...new Set(currentPayDays.map((day) => Math.min(day, last)))].sort((a, b) => a - b);
}

function dayBefore(year: number, month: number, day: number): YearMonthDay {
  if (day > 1) return { year, month, day: day - 1 };
  const previousMonth = month === 1 ? 12 : month - 1;
  const previousYear = month === 1 ? year - 1 : year;
  return { year: previousYear, month: previousMonth, day: lastDayOfMonth(previousYear, previousMonth) };
}

function nextLanding(year: number, month: number, day: number): YearMonthDay {
  const thisMonth = landings(year, month);
  const next = thisMonth.find((landing) => landing > day);
  if (next != null) return { year, month, day: next };
  const nextMonth = month === 12 ? 1 : month + 1;
  const nextYear = month === 12 ? year + 1 : year;
  return { year: nextYear, month: nextMonth, day: landings(nextYear, nextMonth)[0] ?? 1 };
}

function periodFor({ year, month, day }: YearMonthDay): PaycheckPeriod {
  const last = lastDayOfMonth(year, month);
  const clamped = Math.min(Math.max(day, 1), last);
  const thisMonth = landings(year, month);
  let startYear = year;
  let startMonth = month;
  let startDay: number;
  const landing = [...thisMonth].reverse().find((value) => value <= clamped);
  if (landing != null) {
    startDay = landing;
  } else {
    startMonth = month === 1 ? 12 : month - 1;
    startYear = month === 1 ? year - 1 : year;
    const previous = landings(startYear, startMonth);
    startDay = previous[previous.length - 1] ?? 1;
  }
  const next = nextLanding(startYear, startMonth, startDay);
  const end = dayBefore(next.year, next.month, next.day);
  return {
    start: ymd(startYear, startMonth, startDay),
    end: ymd(end.year, end.month, end.day),
  };
}

/** The period covering a date, given either a clock time or a ledger date. */
export function periodContaining(date: Date | string = new Date()): PaycheckPeriod {
  if (typeof date === 'string') {
    const parts = parseLedgerDate(date);
    return periodFor(parts ?? partsInLedgerZone(new Date()));
  }
  return periodFor(partsInLedgerZone(date));
}

/** The whole calendar month a period sits in. */
export function monthContaining(period: PaycheckPeriod): PaycheckPeriod {
  const parts = parseLedgerDate(period.start);
  if (!parts) return period;
  const { year, month } = parts;
  return {
    start: ymd(year, month, 1),
    end: ymd(year, month, lastDayOfMonth(year, month)),
    span: 'month',
  };
}

/** Which landing this period is in its start month, or `null` if it is not one. */
export function payIndexOfPeriod(period: PaycheckPeriod): number | null {
  const parts = parseLedgerDate(period.start);
  if (!parts) return null;
  const index = landings(parts.year, parts.month).indexOf(parts.day);
  return index >= 0 ? index : null;
}

/** Whether this is the first paycheck that lands in its start month. */
export function isFirstPaycheckOfMonth(period: PaycheckPeriod): boolean {
  return payIndexOfPeriod(period) === 0;
}

/** Whether this stretch includes that day-of-month. Cross-month periods check both months. */
export function periodCoversDay(period: PaycheckPeriod, day: number): boolean {
  const start = parseLedgerDate(period.start);
  const end = parseLedgerDate(period.end);
  if (!start || !end) return false;
  const dateInMonth = (year: number, month: number): string => {
    const last = lastDayOfMonth(year, month);
    return ymd(year, month, Math.min(Math.max(Math.trunc(day) || 1, 1), last));
  };
  const startMonthDate = dateInMonth(start.year, start.month);
  if (startMonthDate >= period.start && startMonthDate <= period.end) return true;
  if (start.year !== end.year || start.month !== end.month) {
    const endMonthDate = dateInMonth(end.year, end.month);
    if (endMonthDate >= period.start && endMonthDate <= period.end) return true;
  }
  return false;
}

/** Paychecks that land in this period's calendar month, in order. */
export function paychecksOfMonth(period: PaycheckPeriod): PaycheckPeriod[] {
  const parts = parseLedgerDate(period.start);
  if (!parts) return [period];
  const { year, month } = parts;
  const seen = new Set<string>();
  const result: PaycheckPeriod[] = [];
  for (const day of landings(year, month)) {
    const next = periodFor({ year, month, day });
    if (!seen.has(next.start)) {
      seen.add(next.start);
      result.push(next);
    }
  }
  return result.length > 0 ? result : [period];
}

export function previousPeriod(period: PaycheckPeriod): PaycheckPeriod {
  const parts = parseLedgerDate(period.start);
  if (!parts) return period;
  const { year, month, day } = parts;
  if (period.span === 'month') {
    const previousMonth = month === 1 ? 12 : month - 1;
    const previousYear = month === 1 ? year - 1 : year;
    return {
      start: ymd(previousYear, previousMonth, 1),
      end: ymd(previousYear, previousMonth, lastDayOfMonth(previousYear, previousMonth)),
      span: 'month',
    };
  }
  const prior = dayBefore(year, month, day);
  return periodFor(prior);
}

export function nextPeriod(period: PaycheckPeriod): PaycheckPeriod {
  const parts = parseLedgerDate(period.start);
  if (!parts) return period;
  const { year, month, day } = parts;
  const next = nextLanding(year, month, day);
  return periodFor(next);
}

export function periodContains(period: PaycheckPeriod, ledgerDate: string): boolean {
  const date = ledgerDate.slice(0, 10);
  return date >= period.start && date <= period.end;
}

export function periodsEqual(a: PaycheckPeriod, b: PaycheckPeriod): boolean {
  return a.start === b.start && a.end === b.end;
}

/**
 * "Aug 16 – Aug 31", or "Aug 16 – Aug 31, 2024" for a period in another year.
 *
 * The year is stated once rather than on both ends: a paycheck period runs 1–15
 * or 16–end of month, so it can never straddle a year boundary.
 */
export function periodLabel(period: PaycheckPeriod, now: Date = new Date()): string {
  const end = parseLedgerDate(period.end);
  if (!end) return `${period.start} – ${period.end}`;
  const range = `${monthDay(period.start)} – ${monthDay(period.end)}`;
  return end.year === ledgerYear(now) ? range : `${range}, ${end.year}`;
}

export function isCurrentPeriod(period: PaycheckPeriod, now: Date = new Date()): boolean {
  return periodsEqual(period, periodContaining(now));
}

/**
 * What the paycheck has left once bills and envelopes are funded. Settled in
 * whole cents so a plan that balances exactly reads as $0.00, not -$0.00.
 */
export function paycheckRemainder(
  paycheckAmount: number,
  billAllocation: number,
  envelopeAllocation: number
): number {
  return roundCents(paycheckAmount - billAllocation - envelopeAllocation);
}

/** The next landing after today. Today does not count, even if it is a pay day. */
export function nextPaycheckDate(now: Date = new Date()): string {
  const { year, month, day } = partsInLedgerZone(now);
  const next = nextLanding(year, month, day);
  return ymd(next.year, next.month, next.day);
}

export function daysUntilNextPaycheck(now: Date = new Date()): number {
  return Math.max(0, daysBetweenLedgerDates(ledgerToday(now), nextPaycheckDate(now)));
}

/**
 * How far through the period we are, 0–1. Paces spending against time so a
 * half-spent envelope on day 2 reads differently from one on day 14.
 */
export function periodProgress(period: PaycheckPeriod, now: Date = new Date()): number {
  const total = daysBetweenLedgerDates(period.start, period.end) + 1;
  if (total <= 0) return 0;
  const elapsed = daysBetweenLedgerDates(period.start, ledgerToday(now)) + 1;
  return Math.min(1, Math.max(0, elapsed / total));
}

/** Most recent first, starting with the period containing `now`. */
export function recentPeriods(count: number, now: Date = new Date()): PaycheckPeriod[] {
  const periods: PaycheckPeriod[] = [];
  let period = periodContaining(now);
  for (let index = 0; index < count; index += 1) {
    periods.push(period);
    period = previousPeriod(period);
  }
  return periods;
}
